import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import ImageUploader from "@/components/admin/ImageUploader";

const empty = { name_es: "", name_en: "", description_es: "", description_en: "", image_url: "", sort_order: 0 };

export default function AdminCategories() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/categories").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...editing, sort_order: Number(editing.sort_order) };
      if (editing.id) {
        await api.put(`/categories/${editing.id}`, payload);
      } else {
        await api.post("/categories", payload);
      }
      toast.success("Guardado");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar?")) return;
    const prev = items;
    setItems((xs) => xs.filter((c) => c.id !== id)); // actualización optimista
    try {
      await api.delete(`/categories/${id}`);
      toast.success("Eliminado");
      await load(); // resincroniza con el servidor
    } catch (err) {
      setItems(prev); // revierte si falla
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div data-testid="admin-categories-page">
      <header className="flex items-center justify-between mb-8">
        <div>
          <p className="tracking-eyebrow text-[var(--eafg-muted)]">Catálogo</p>
          <h1 className="font-serif-display text-5xl mt-2">{t("admin.categories")}</h1>
        </div>
        <button
          onClick={() => setEditing({ ...empty })}
          className="bg-[var(--eafg-ink)] text-white px-5 py-3 tracking-eyebrow hover:bg-[var(--eafg-copper)] inline-flex items-center gap-2"
          data-testid="admin-new-category"
        >
          <Plus size={14} strokeWidth={1.5} /> {t("admin.newCategory")}
        </button>
      </header>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((c) => (
          <div key={c.id} className="border border-[var(--eafg-line)] bg-white p-5" data-testid={`admin-cat-${c.slug}`}>
            <div className="aspect-[4/3] bg-[#efece8] mb-4 overflow-hidden">
              {c.image_url && <img src={c.image_url} alt="" className="w-full h-full object-cover" />}
            </div>
            <h3 className="font-serif-display text-2xl">{c.name_es}</h3>
            <p className="text-[var(--eafg-muted)] text-sm mt-1">{c.name_en}</p>
            <div className="flex gap-3 mt-4">
              <button onClick={() => setEditing(c)} className="text-sm tracking-eyebrow hover:text-[var(--eafg-copper)] inline-flex items-center gap-1" data-testid={`admin-edit-cat-${c.slug}`}>
                <Pencil size={12} strokeWidth={1.5} /> {t("admin.edit")}
              </button>
              <button onClick={() => del(c.id)} className="text-sm tracking-eyebrow hover:text-[var(--eafg-copper)] inline-flex items-center gap-1" data-testid={`admin-delete-cat-${c.slug}`}>
                <Trash2 size={12} strokeWidth={1.5} /> {t("admin.delete")}
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-4">
          <form onSubmit={submit} className="bg-white w-full max-w-xl max-h-[90vh] overflow-y-auto p-8 space-y-5 border border-[var(--eafg-ink)]">
            <div className="flex items-center justify-between">
              <h2 className="font-serif-display text-3xl">
                {editing.id ? t("admin.edit") : t("admin.newCategory")}
              </h2>
              <button type="button" onClick={() => setEditing(null)}>
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Nombre (ES)" value={editing.name_es} onChange={(v) => setEditing({ ...editing, name_es: v })} required testId="cf-name-es" />
              <Field label="Name (EN)" value={editing.name_en} onChange={(v) => setEditing({ ...editing, name_en: v })} required testId="cf-name-en" />
            </div>
            <Field label="Descripción (ES)" textarea value={editing.description_es} onChange={(v) => setEditing({ ...editing, description_es: v })} testId="cf-desc-es" />
            <Field label="Description (EN)" textarea value={editing.description_en} onChange={(v) => setEditing({ ...editing, description_en: v })} testId="cf-desc-en" />

            {/* Single image uploader for category */}
            <ImageUploader
              images={editing.image_url ? [editing.image_url] : []}
              onChange={([url] = []) => setEditing({ ...editing, image_url: url || "" })}
              multiple={false}
            />

            <Field label="Orden" type="number" value={editing.sort_order} onChange={(v) => setEditing({ ...editing, sort_order: v })} testId="cf-order" />

            <div className="flex gap-3 pt-4 border-t border-[var(--eafg-line)]">
              <button type="submit" className="bg-[var(--eafg-copper)] text-white px-6 py-3 tracking-eyebrow" data-testid="cf-save">
                {t("admin.save")}
              </button>
              <button type="button" onClick={() => setEditing(null)} className="border border-[var(--eafg-ink)] px-6 py-3 tracking-eyebrow">
                {t("admin.cancel")}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testId, textarea = false }) {
  return (
    <label className="block">
      <span className="tracking-eyebrow text-[var(--eafg-muted)] block mb-2">{label}</span>
      {textarea ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3}
          className="w-full border border-[var(--eafg-line)] p-2 bg-transparent" data-testid={testId} />
      ) : (
        <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)}
          className="w-full border-b border-[var(--eafg-line)] py-2 bg-transparent" data-testid={testId} />
      )}
    </label>
  );
}
